<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useAttrs } from "vue";
import ArgonButton from "@/components/ArgonButton.vue";

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits(["action"]);
const OPEN_EVENT_NAME = "shared-actions-dropdown:open";
const attrs = useAttrs();

const props = defineProps({
  menuId: { type: String, required: true },
  actions: {
    type: Array,
    default() {
      return [];
    },
  },
  ariaLabel: { type: String, default: "Acciones" },
  iconClass: { type: String, default: "fas fa-ellipsis-h" },
  triggerClass: { type: String, default: "" },
  menuClass: { type: String, default: "" },
  menuGap: { type: String, default: "10px" },
  hoverRevealScope: { type: String, default: "" },
  triggerType: { type: String, default: "icon" },
  buttonLabel: { type: String, default: "Acciones" },
  buttonIconClass: { type: String, default: "" },
  buttonColor: { type: String, default: "primary" },
  buttonVariant: { type: String, default: "outline" },
  buttonClass: { type: String, default: "" },
});

const triggerRef = ref(null);
const menuRef = ref(null);
const rootRef = ref(null);
const menuPlacement = ref("right");
const isOpen = ref(false);
const isInTableContext = ref(false);
const isHoverRevealActive = ref(false);
const OPEN_STATE_CLASS = "kanban-dropdown-open";
const isButtonTrigger = computed(() => props.triggerType === "button");
const normalizedActionGroups = computed(() =>
  (Array.isArray(props.actions) ? props.actions : [])
    .map((group) =>
      Array.isArray(group)
        ? group.filter((action) => action && typeof action === "object")
        : []
    )
    .filter((group) => group.length > 0)
);

function onAction(action) {
  if (action?.disabled) return;
  emit("action", action);
  closeDropdownMenu();
}

function closeDropdownMenu() {
  if (!isOpen.value) return;
  isOpen.value = false;
  syncDropdownAncestors(false);
}

function closeOpenBootstrapDropdowns() {
  const openedMenus = document.querySelectorAll(".dropdown-menu.show");
  openedMenus.forEach((menuElement) => {
    if (rootRef.value?.contains(menuElement)) return;
    menuElement.classList.remove("show");
    const dropdownRoot = menuElement.closest(".dropdown");
    dropdownRoot?.classList.remove("show");
    const trigger = dropdownRoot?.querySelector(
      "[data-bs-toggle='dropdown'], .dropdown-toggle"
    );
    trigger?.setAttribute("aria-expanded", "false");
  });
}

function joinClasses(...classes) {
  return classes.filter(Boolean).join(" ");
}

function getActionType(action) {
  const type = String(action?.type || "normal")
    .trim()
    .toLowerCase();
  if (type === "warning" || type === "danger") return type;
  return "normal";
}

function getActionClass(action) {
  return joinClasses(
    "actions-dropdown-item",
    `actions-dropdown-item--${getActionType(action)}`,
    action?.disabled ? "disabled" : ""
  );
}

function getDropdownAncestors() {
  if (!triggerRef.value) return [];

  const selectors = [
    ".kanban-dp-board",
    ".kanban-dp-item",
    ".kanban-board-header",
    ".stop-route-card",
    ".route-time-window-cell",
    ".route-stop-status-cell",
    "td",
    "tr",
  ];

  return selectors
    .map((selector) => triggerRef.value?.closest(selector))
    .filter(
      (element, index, elements) =>
        element && elements.indexOf(element) === index
    );
}

function syncDropdownAncestors(isOpenValue) {
  getDropdownAncestors().forEach((element) => {
    element.classList.toggle(OPEN_STATE_CLASS, isOpenValue);
  });
}

function getViewportWidth() {
  return window.innerWidth || document.documentElement.clientWidth || 0;
}

function getMenuGapPx() {
  const parsedGap = Number.parseFloat(String(props.menuGap || "").trim());
  return Number.isFinite(parsedGap) ? parsedGap : 4;
}

function refreshContext() {
  isInTableContext.value = Boolean(
    triggerRef.value?.closest(".table-responsive")
  );
}

function updateMenuPlacement() {
  if (!triggerRef.value || !menuRef.value) return;

  if (isButtonTrigger.value) {
    const triggerRect = triggerRef.value.getBoundingClientRect();
    const menuRect = menuRef.value.getBoundingClientRect();
    const viewportWidth = getViewportWidth();
    const gap = getMenuGapPx();
    const spaceRight = Math.max(0, viewportWidth - triggerRect.left);
    const requiredWidth = menuRect.width + gap;

    menuPlacement.value = spaceRight >= requiredWidth ? "right" : "left";
    return;
  }

  if (isInTableContext.value) {
    menuPlacement.value = "left";
    return;
  }

  const triggerRect = triggerRef.value.getBoundingClientRect();
  const menuRect = menuRef.value.getBoundingClientRect();
  const viewportWidth = getViewportWidth();
  const gap = getMenuGapPx();
  const spaceRight = Math.max(0, viewportWidth - triggerRect.right);
  const requiredWidth = menuRect.width + gap;

  menuPlacement.value = spaceRight >= requiredWidth ? "right" : "left";
}

function syncMenuPlacement() {
  requestAnimationFrame(() => {
    updateMenuPlacement();
  });
}

function onWindowResize() {
  refreshContext();
  if (!isOpen.value) return;
  syncMenuPlacement();
}

function openDropdownMenu() {
  if (isOpen.value) return;
  window.dispatchEvent(
    new CustomEvent(OPEN_EVENT_NAME, { detail: { menuId: props.menuId } })
  );
  closeOpenBootstrapDropdowns();
  refreshContext();
  isOpen.value = true;
  syncDropdownAncestors(true);
  nextTick(() => {
    syncMenuPlacement();
  });
}

function toggleDropdownMenu() {
  if (isOpen.value) {
    closeDropdownMenu();
    return;
  }
  openDropdownMenu();
}

function onClickOutside(event) {
  if (!isOpen.value || !rootRef.value) return;
  if (!rootRef.value.contains(event.target)) {
    closeDropdownMenu();
  }
}

function onKeydown(event) {
  if (event.key === "Escape") {
    closeDropdownMenu();
  }
}

function onAnotherDropdownOpened(event) {
  const openedMenuId = event?.detail?.menuId;
  if (!openedMenuId || openedMenuId === props.menuId) return;
  closeDropdownMenu();
}

let hoverScopeElement = null;

function setHoverRevealState(isActive) {
  isHoverRevealActive.value = Boolean(isActive);
}

function bindHoverRevealScope() {
  const selector = String(props.hoverRevealScope || "").trim();
  if (!selector || !triggerRef.value || isButtonTrigger.value) return;
  hoverScopeElement = triggerRef.value.closest(selector);
  if (!hoverScopeElement) return;

  hoverScopeElement.addEventListener("mouseenter", onHoverRevealEnter);
  hoverScopeElement.addEventListener("mouseleave", onHoverRevealLeave);
  hoverScopeElement.addEventListener("focusin", onHoverRevealEnter);
  hoverScopeElement.addEventListener("focusout", onHoverRevealLeave);
}

function unbindHoverRevealScope() {
  if (!hoverScopeElement) return;
  hoverScopeElement.removeEventListener("mouseenter", onHoverRevealEnter);
  hoverScopeElement.removeEventListener("mouseleave", onHoverRevealLeave);
  hoverScopeElement.removeEventListener("focusin", onHoverRevealEnter);
  hoverScopeElement.removeEventListener("focusout", onHoverRevealLeave);
  hoverScopeElement = null;
}

function onHoverRevealEnter() {
  setHoverRevealState(true);
}

function onHoverRevealLeave() {
  if (isOpen.value) return;
  setHoverRevealState(false);
}

onMounted(() => {
  refreshContext();
  bindHoverRevealScope();
  document.addEventListener("click", onClickOutside, true);
  document.addEventListener("keydown", onKeydown);
  window.addEventListener(OPEN_EVENT_NAME, onAnotherDropdownOpened);
  window.addEventListener("resize", onWindowResize);
});

onBeforeUnmount(() => {
  syncDropdownAncestors(false);
  unbindHoverRevealScope();
  document.removeEventListener("click", onClickOutside, true);
  document.removeEventListener("keydown", onKeydown);
  window.removeEventListener(OPEN_EVENT_NAME, onAnotherDropdownOpened);
  window.removeEventListener("resize", onWindowResize);
});
</script>

<template>
  <div
    v-bind="attrs"
    ref="rootRef"
    :class="
      joinClasses(
        'actions-dropdown',
        'dropdown',
        'd-inline',
        isButtonTrigger ? 'actions-dropdown--button' : 'actions-dropdown--icon'
      )
    "
    :style="{ '--dropdown-gap': menuGap }"
    @click.stop
  >
    <argon-button
      v-if="isButtonTrigger"
      :id="menuId"
      ref="triggerRef"
      :color="buttonColor"
      :variant="buttonVariant"
      :class="
        joinClasses(
          'dropdown-toggle',
          'btn-xs',
          buttonClass,
          triggerClass
        )
      "
      :aria-expanded="isOpen"
      :aria-label="ariaLabel"
      @click.stop.prevent="toggleDropdownMenu"
    >
      <i v-if="buttonIconClass" :class="[buttonIconClass, 'me-sm-2']"></i
      >{{ buttonLabel }}
    </argon-button>
    <button
      v-else
      :id="menuId"
      ref="triggerRef"
      type="button"
      :class="
        joinClasses(
          'task-actions-button',
          triggerClass,
          hoverRevealScope ? 'task-actions-button--hover-reveal' : '',
          !hoverRevealScope || isHoverRevealActive || isOpen
            ? 'task-actions-button--visible'
            : ''
        )
      "
      :aria-expanded="isOpen"
      :aria-label="ariaLabel"
      @click.stop.prevent="toggleDropdownMenu"
    >
      <i :class="iconClass"></i>
    </button>
    <ul
      ref="menuRef"
      :class="
        joinClasses(
          'dropdown-menu px-2 actions-dropdown-menu',
          isOpen ? 'actions-dropdown-menu--open' : '',
          `actions-dropdown-menu--${menuPlacement}`,
          isInTableContext ? 'actions-dropdown-menu--table' : '',
          menuClass
        )
      "
      :aria-labelledby="menuId"
    >
      <template
        v-for="(group, groupIdx) in normalizedActionGroups"
        :key="`${menuId}-group-${groupIdx}`"
      >
        <li class="actions-dropdown-group">
          <button
            v-for="(action, actionIdx) in group"
            :key="`${menuId}-action-${groupIdx}-${actionIdx}`"
            v-tooltip="action?.tooltip || ''"
            type="button"
            :class="getActionClass(action)"
            :disabled="Boolean(action?.disabled)"
            @click.stop.prevent="onAction(action)"
          >
            <i v-if="action?.icon" :class="[action.icon, 'me-3']"></i
            >{{ action?.label || "Acción" }}
          </button>
        </li>
        <li
          v-if="groupIdx < normalizedActionGroups.length - 1"
          class="actions-dropdown-divider-wrapper"
        >
          <hr class="dropdown-divider actions-dropdown-divider" />
        </li>
      </template>
    </ul>
  </div>
</template>

<style scoped>
.actions-dropdown {
  position: relative;
  z-index: 20;
}

.actions-dropdown--icon {
  align-items: flex-start;
  display: inline-flex;
  vertical-align: top;
}

.actions-dropdown--button {
  vertical-align: top;
}

.actions-dropdown > button:not(.task-actions-button) {
  background: transparent;
  border: 0;
  padding: 0;
}

.actions-dropdown--icon > button.task-actions-button {
  align-items: center;
  background: #fff;
  border: 1px solid #fff;
  border-radius: 8px;  
  color: #212529;
  display: inline-flex;
  height: 22px;
  justify-content: center;
  left: 0;
  margin-right: 0 !important;
  padding: 0;
  position: absolute;
  text-decoration: none;
  top: 0;
  width: 22px;
}

.actions-dropdown--icon > button.task-actions-button.task-actions-button--hover-reveal {
  opacity: 0;
}

.actions-dropdown--icon > button.task-actions-button.task-actions-button--visible {
  opacity: 1;
}

.actions-dropdown-menu {
  background-color: #fff !important;
  border: 1px solid #fff !important;
  border-radius: 10px !important;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.1), 0 1px 4px rgba(15, 23, 42, 0.06) !important;
  display: block !important;
  margin-top: 0 !important;
  min-width: 180px;
  opacity: 0 !important;
  overflow: hidden;
  padding: 4px !important;
  pointer-events: none !important;
  visibility: hidden !important;
  z-index: 30 !important;
}

.actions-dropdown-menu.actions-dropdown-menu--open {
  opacity: 1 !important;
  pointer-events: auto !important;
  visibility: visible !important;
}

.actions-dropdown--icon > .actions-dropdown-menu {
  left: calc(100% + var(--dropdown-gap)) !important;
  right: auto !important;
  top: -10px !important;
}

.actions-dropdown--icon
  > .actions-dropdown-menu[data-bs-popper] {
  inset: -10px auto auto calc(100% + var(--dropdown-gap)) !important;
  transform: none !important;
}

.actions-dropdown--icon
  > .actions-dropdown-menu--left.actions-dropdown-menu {
  left: auto !important;
  right: calc(100% + var(--dropdown-gap)) !important;
}

.actions-dropdown--icon
  > .actions-dropdown-menu--left.actions-dropdown-menu[data-bs-popper] {
  inset: -10px calc(100% + var(--dropdown-gap)) auto auto !important;
}

.actions-dropdown--icon
  > .actions-dropdown-menu--table.actions-dropdown-menu {
  left: auto !important;
  right: 0 !important;
  top: -10px !important;
}

.actions-dropdown--icon
  > .actions-dropdown-menu--table.actions-dropdown-menu[data-bs-popper] {
  inset: -10px 0 auto auto !important;
  transform: none !important;
}

.actions-dropdown--icon
  > .actions-dropdown-menu--table.actions-dropdown-menu--left.actions-dropdown-menu.actions-dropdown-menu--open {
  left: auto !important;
  right: calc(100% + var(--dropdown-gap)) !important;
}

.actions-dropdown--icon
  > .actions-dropdown-menu--table.actions-dropdown-menu--left.actions-dropdown-menu[data-bs-popper].actions-dropdown-menu--open {
  inset: -10px calc(100% + var(--dropdown-gap)) auto auto !important;
}

.actions-dropdown--button > .actions-dropdown-menu {
  left: 0 !important;
  right: auto !important;
  top: calc(100% + 4px) !important;
}

.actions-dropdown--button
  > .actions-dropdown-menu--left.actions-dropdown-menu {
  left: auto !important;
  right: 0 !important;
}

.actions-dropdown-group {
  display: flex;
  flex-direction: column;
}

.actions-dropdown-item {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 7px;
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  font-size: 0.825rem;
  font-weight: 400;
  line-height: 1.25;
  margin: 0;
  min-height: 32px;
  padding: 6px 10px;
  text-align: left;
  transition: background-color 0.15s ease, color 0.15s ease;
  width: 100%;
}

.actions-dropdown-item:hover,
.actions-dropdown-item:focus-visible {
  background-color: #f1f5f9;
  color: #1e293b;
  outline: none;
}

.actions-dropdown-item i {
  color: inherit;
  width: 16px;
}

.actions-dropdown-item--warning {
  color: #b7791f;
}

.actions-dropdown-item--danger {
  color: #dc2626;
}

.actions-dropdown-item.disabled,
.actions-dropdown-item:disabled {
  background-color: transparent;
  color: #b9c0cb;
  cursor: not-allowed;
}

.actions-dropdown-item.disabled:hover,
.actions-dropdown-item.disabled:focus-visible,
.actions-dropdown-item:disabled:hover,
.actions-dropdown-item:disabled:focus-visible {
  background-color: transparent;
  color: #b9c0cb;
  outline: none;
}

.actions-dropdown-divider-wrapper {
  padding: 3px 0;
}

.actions-dropdown-divider {
  border-top: 1px solid #e2e8f0 !important;
  margin: 0 4px;
  opacity: 1;
}
</style>
